import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stage, Layer, Image as KonvaImage, Arrow, Circle, Text, Label, Tag } from 'react-konva';

const MAX_ARROWS = 10;
const ARROW_COLOR = '#FF5722';
const HANDLE_RADIUS = 8;

const readStoredArrows = (storageKey, legacyStorageKey) => {
  try {
    const stored = localStorage.getItem(storageKey) || localStorage.getItem(legacyStorageKey);
    const parsed = stored ? JSON.parse(stored) : [];
    return parsed.filter((a) => a.x1 !== undefined);
  } catch {
    return [];
  }
};

const MapCanvas = ({ slug, imageUrl, backLink, mapName }) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);

  const [image, setImage] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 450 });
  const storageKey = `map-annotations-${slug}`;
  const legacyStorageKey = `cpc-annotations-${slug}`;

  const [arrows, setArrows] = useState(() => readStoredArrows(storageKey, legacyStorageKey));

  const [selectedId, setSelectedId] = useState(null);
  const [labelInput, setLabelInput] = useState('');
  const [colorInput, setColorInput] = useState(ARROW_COLOR);
  const [drawingMode, setDrawingMode] = useState(false);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const arrowDragStartRef = useRef(null);
  const loadedSlugRef = useRef(slug);

  // Always-current mirror of arrows state — avoids stale closures in event handlers
  const arrowsRef = useRef(null);
  const historyRef = useRef(null);
  const historyIndexRef = useRef(0);

  // Initialize refs once on first render using initial arrows value
  if (arrowsRef.current === null) arrowsRef.current = arrows;
  if (historyRef.current === null) historyRef.current = [arrows];

  // Keep arrowsRef in sync with state
  useEffect(() => { arrowsRef.current = arrows; }, [arrows]);

  useEffect(() => {
    if (loadedSlugRef.current === slug) return;

    const storedArrows = readStoredArrows(storageKey, legacyStorageKey);
    loadedSlugRef.current = slug;
    arrowsRef.current = storedArrows;
    historyRef.current = [storedArrows];
    historyIndexRef.current = 0;
    setArrows(storedArrows);
    setSelectedId(null);
    setCanUndo(false);
    setCanRedo(false);
  }, [slug, storageKey, legacyStorageKey]);

  const updateStageSize = useCallback((img) => {
    if (!containerRef.current || !img) return;

    const ratio = img.height / img.width;
    const maxH = 600;
    let w = containerRef.current.offsetWidth;
    let h = Math.round(w * ratio);

    if (h > maxH) {
      h = maxH;
      w = Math.round(maxH / ratio);
    }

    setStageSize({ width: w, height: h });
  }, []);

  // Disable page scroll while drawing so drag doesn't fight scroll
  useEffect(() => {
    document.body.style.overflowY = drawingMode ? 'hidden' : '';
    return () => { document.body.style.overflowY = ''; };
  }, [drawingMode]);

  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    img.onload = () => {
      setImage(img);
      updateStageSize(img);
    };
  }, [imageUrl, updateStageSize]);

  useEffect(() => {
    if (!image) return;
    updateStageSize(image);

    const handleResize = () => updateStageSize(image);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [image, updateStageSize]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(arrows));
  }, [arrows, storageKey]);

  useEffect(() => {
    if (selectedId) {
      const a = arrows.find((a) => a.id === selectedId);
      setLabelInput(a?.label ?? '');
      setColorInput(a?.color ?? ARROW_COLOR);
    } else {
      setLabelInput('');
      setColorInput(ARROW_COLOR);
    }
  }, [selectedId]); // arrows omitted intentionally — avoids cursor jump while typing

  const updateArrow = useCallback((id, props) => {
    setArrows((prev) => prev.map((a) => (a.id === id ? { ...a, ...props } : a)));
  }, []);

  // ── History ───────────────────────────────────────────────────
  const pushHistory = (newArrows) => {
    const h = historyRef.current;
    const idx = historyIndexRef.current;
    const next = [...h.slice(0, idx + 1), newArrows];
    historyRef.current = next;
    historyIndexRef.current = next.length - 1;
    setCanUndo(true);
    setCanRedo(false);
  };

  const undo = useCallback(() => {
    const idx = historyIndexRef.current;
    if (idx <= 0) return;
    const newIdx = idx - 1;
    historyIndexRef.current = newIdx;
    const newArrows = historyRef.current[newIdx];
    setArrows(newArrows);
    arrowsRef.current = newArrows;
    setSelectedId(null);
    setCanUndo(newIdx > 0);
    setCanRedo(true);
  }, []);

  const redo = useCallback(() => {
    const idx = historyIndexRef.current;
    const h = historyRef.current;
    if (idx >= h.length - 1) return;
    const newIdx = idx + 1;
    historyIndexRef.current = newIdx;
    const newArrows = h[newIdx];
    setArrows(newArrows);
    arrowsRef.current = newArrows;
    setSelectedId(null);
    setCanUndo(true);
    setCanRedo(newIdx < h.length - 1);
  }, []);

  // ── Keyboard shortcuts ────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (document.activeElement.tagName === 'INPUT') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        deleteSelected();
      } else if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId, undo, redo]); // selectedId needed for deleteSelected closure

  // ── Drawing mode ──────────────────────────────────────────────
  const enterDrawingMode = () => {
    if (arrows.length >= MAX_ARROWS) return;
    setSelectedId(null);
    setDrawingMode(true);
  };

  const getStagePos = (e) => {
    const stage = e.target.getStage ? e.target.getStage() : stageRef.current;
    return stage.getPointerPosition();
  };

  const handleStageMouseDown = (e) => {
    if (drawingMode) {
      const pos = getStagePos(e);
      setPreview({ x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
      return;
    }
    const name = e.target.name();
    if (e.target === e.target.getStage() || name === 'map-bg') {
      setSelectedId(null);
    }
  };

  const handleStageMouseMove = (e) => {
    if (!drawingMode || !preview) return;
    const pos = getStagePos(e);
    setPreview((p) => ({ ...p, x2: pos.x, y2: pos.y }));
  };

  const handleStageMouseUp = () => {
    if (drawingMode && preview) {
      const dx = preview.x2 - preview.x1;
      const dy = preview.y2 - preview.y1;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 10) {
        const id = `arrow-${Date.now()}`;
        const newArrow = { id, x1: preview.x1, y1: preview.y1, x2: preview.x2, y2: preview.y2, label: `Attacker ${arrowsRef.current.length + 1}`, color: ARROW_COLOR };
        const newArrows = [...arrowsRef.current, newArrow];
        setArrows(newArrows);
        pushHistory(newArrows);
        setSelectedId(id);
      }
      setPreview(null);
      setDrawingMode(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────
  const deleteSelected = () => {
    if (!selectedId) return;
    const newArrows = arrowsRef.current.filter((a) => a.id !== selectedId);
    setArrows(newArrows);
    pushHistory(newArrows);
    setSelectedId(null);
  };

  // ── Arrow line drag ────────────────────────────────────────────
  const handleArrowDragStart = (e, arrow) => {
    const pos = e.target.getStage().getPointerPosition();
    arrowDragStartRef.current = {
      pointerX: pos.x, pointerY: pos.y,
      x1: arrow.x1, y1: arrow.y1, x2: arrow.x2, y2: arrow.y2,
    };
    e.target.position({ x: 0, y: 0 });
  };

  const handleArrowDragMove = (e, id) => {
    if (!arrowDragStartRef.current) return;
    const pos = e.target.getStage().getPointerPosition();
    const dx = pos.x - arrowDragStartRef.current.pointerX;
    const dy = pos.y - arrowDragStartRef.current.pointerY;
    const start = arrowDragStartRef.current;
    setArrows((prev) => prev.map((a) => a.id === id ? {
      ...a,
      x1: start.x1 + dx, y1: start.y1 + dy,
      x2: start.x2 + dx, y2: start.y2 + dy,
    } : a));
    e.target.position({ x: 0, y: 0 });
  };

  const handleArrowDragEnd = (e) => {
    arrowDragStartRef.current = null;
    e.target.position({ x: 0, y: 0 });
    pushHistory(arrowsRef.current);
  };

  // ── Label / color ──────────────────────────────────────────────
  const handleLabelChange = (e) => {
    const val = e.target.value;
    setLabelInput(val);
    if (selectedId) updateArrow(selectedId, { label: val });
  };

  const handleColorChange = (e) => {
    const val = e.target.value;
    setColorInput(val);
    if (selectedId) updateArrow(selectedId, { color: val });
  };

  // ── Copy / download ────────────────────────────────────────────
  const getCanvasBlob = async () => {
    const dataUrl = stageRef.current.toDataURL({ pixelRatio: window.devicePixelRatio || 1 });
    return {
      dataUrl,
      blob: await (await fetch(dataUrl)).blob(),
    };
  };

  const saveMapLog = async (action, blob) => {
    try {
      const formData = new FormData();
      formData.append('image', blob, `${slug}-${action}.png`);
      formData.append('slug', slug);
      formData.append('mapName', mapName || slug);
      formData.append('action', action);

      await fetch('/save-map-log.php', {
        method: 'POST',
        body: formData,
      });
    } catch {
      // Export logging is best-effort; copy/download should still work.
    }
  };

  const copyToClipboard = async () => {
    if (!stageRef.current) return;
    try {
      const { dataUrl, blob } = await getCanvasBlob();
      void saveMapLog('copy', blob);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const dataUrl = stageRef.current.toDataURL();
      window.open(dataUrl);
    }
  };

  const downloadCanvas = async () => {
    if (!stageRef.current) return;
    const { dataUrl, blob } = await getCanvasBlob();
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${slug}.png`;
    a.click();
    void saveMapLog('download', blob);
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 2000);
  };

  const midpoint = (a) => ({
    x: (a.x1 + a.x2) / 2,
    y: (a.y1 + a.y2) / 2,
  });

  return (
    <div ref={containerRef} className="w-full">
      <div style={{ width: stageSize.width }} className="mx-auto">
        {/* Back link */}
        {backLink && <div className="mb-4">{backLink}</div>}

        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button
            onClick={enterDrawingMode}
            disabled={arrows.length >= MAX_ARROWS || drawingMode}
            title={
              arrows.length >= MAX_ARROWS
                ? `Maximum ${MAX_ARROWS} arrows`
                : drawingMode
                ? 'Click and drag on the map to draw'
                : 'Add arrow'
            }
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors ${
              drawingMode
                ? 'bg-yellow-500'
                : 'bg-indigo-600 dark:bg-[#FF5722] hover:bg-indigo-700 dark:hover:bg-[#E64A19] disabled:opacity-40 disabled:cursor-not-allowed'
            }`}
          >
            <span className="text-lg leading-none">+</span>
            <span>{drawingMode ? 'Drawing… click & drag' : `Add Arrow (${arrows.length}/${MAX_ARROWS})`}</span>
          </button>

          <button
            onClick={deleteSelected}
            disabled={!selectedId}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-600 transition-colors"
          >
            🗑 Delete
          </button>

          <button
            onClick={() => { const e = []; setArrows(e); pushHistory(e); setSelectedId(null); }}
            disabled={arrows.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
          >
            ↺ Reset
          </button>

          {/* Undo / Redo */}
          <button
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-200 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-[#383838] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
            </svg>
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Y)"
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-200 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-[#383838] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
            </svg>
          </button>

          {selectedId && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Label:
                </label>
                <input
                  type="text"
                  value={labelInput}
                  onChange={handleLabelChange}
                  onBlur={() => pushHistory(arrowsRef.current)}
                  placeholder="Arrow label..."
                  maxLength={40}
                  className="border border-gray-300 dark:border-[#444] bg-white dark:bg-[#1A1A1A] text-gray-900 dark:text-white rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-[#FF5722] w-48"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Color:
                </label>
                <input
                  type="color"
                  value={colorInput}
                  onChange={handleColorChange}
                  onBlur={() => pushHistory(arrowsRef.current)}
                  className="w-8 h-8 rounded cursor-pointer border border-gray-300 dark:border-[#444] bg-transparent p-0.5"
                />
              </div>
            </div>
          )}

          {/* Copy / Download */}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={copyToClipboard}
              disabled={!image}
              title={copied ? 'Copied!' : 'Copy to clipboard'}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {copied ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              )}
            </button>
            <button
              onClick={downloadCanvas}
              disabled={!image}
              title={downloaded ? 'Downloaded!' : 'Download as PNG'}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-600 dark:bg-[#FF5722] text-white hover:bg-indigo-700 dark:hover:bg-[#E64A19] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {downloaded ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div>
          <div
            style={{ cursor: drawingMode ? 'crosshair' : 'default' }}
            className="rounded-2xl overflow-hidden border border-gray-200 dark:border-[#2A2A2A] shadow-lg"
          >
            <Stage
              ref={stageRef}
              width={stageSize.width}
              height={stageSize.height}
              onMouseDown={handleStageMouseDown}
              onMouseMove={handleStageMouseMove}
              onMouseUp={handleStageMouseUp}
              onTouchStart={handleStageMouseDown}
              onTouchMove={handleStageMouseMove}
              onTouchEnd={handleStageMouseUp}
            >
              {/* Layer 1: background — not interactive */}
              <Layer listening={false}>
                {image && (
                  <KonvaImage
                    name="map-bg"
                    image={image}
                    width={stageSize.width}
                    height={stageSize.height}
                  />
                )}
                {mapName && (
                  <Label x={12} y={stageSize.height - 36}>
                    <Tag fill="rgba(0,0,0,0.55)" cornerRadius={4} />
                    <Text text={mapName} fontSize={14} fill="white" fontStyle="bold" padding={5} />
                  </Label>
                )}
              </Layer>

              {/* Layer 2: arrows + handles */}
              <Layer>
                {preview && (
                  <Arrow
                    points={[preview.x1, preview.y1, preview.x2, preview.y2]}
                    stroke={ARROW_COLOR}
                    fill={ARROW_COLOR}
                    strokeWidth={3}
                    pointerLength={12}
                    pointerWidth={10}
                    opacity={0.6}
                    listening={false}
                  />
                )}

                {arrows.map((arrow) => {
                  const isSelected = selectedId === arrow.id;
                  const mid = midpoint(arrow);
                  const color = arrow.color ?? ARROW_COLOR;

                  return (
                    <React.Fragment key={arrow.id}>
                      <Arrow
                        name="arrow-line"
                        x={0}
                        y={0}
                        points={[arrow.x1, arrow.y1, arrow.x2, arrow.y2]}
                        stroke={isSelected ? '#FFAB40' : color}
                        fill={isSelected ? '#FFAB40' : color}
                        strokeWidth={isSelected ? 4 : 3}
                        pointerLength={12}
                        pointerWidth={10}
                        hitStrokeWidth={20}
                        draggable={!drawingMode}
                        onClick={() => !drawingMode && setSelectedId(arrow.id)}
                        onTap={() => !drawingMode && setSelectedId(arrow.id)}
                        onDragStart={(e) => handleArrowDragStart(e, arrow)}
                        onDragMove={(e) => handleArrowDragMove(e, arrow.id)}
                        onDragEnd={handleArrowDragEnd}
                      />

                      {arrow.label ? (
                        <Label x={mid.x + 6} y={mid.y - 20} listening={false}>
                          <Tag fill="rgba(0,0,0,0.72)" cornerRadius={4} />
                          <Text text={arrow.label} fontSize={13} fill="white" fontStyle="bold" padding={4} />
                        </Label>
                      ) : null}

                      {isSelected && (
                        <Circle
                          x={arrow.x1} y={arrow.y1}
                          radius={HANDLE_RADIUS}
                          fill="white" stroke={color} strokeWidth={2}
                          draggable
                          onDragMove={(e) => updateArrow(arrow.id, { x1: e.target.x(), y1: e.target.y() })}
                          onDragEnd={() => pushHistory(arrowsRef.current)}
                        />
                      )}

                      {isSelected && (
                        <Circle
                          x={arrow.x2} y={arrow.y2}
                          radius={HANDLE_RADIUS}
                          fill={color} stroke="white" strokeWidth={2}
                          draggable
                          onDragMove={(e) => updateArrow(arrow.id, { x2: e.target.x(), y2: e.target.y() })}
                          onDragEnd={() => pushHistory(arrowsRef.current)}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Layer>
            </Stage>
          </div>
        </div>

        {arrows.length === 0 && image && !drawingMode && (
          <p className="text-center text-sm text-gray-400 dark:text-gray-600 mt-3">
            Click "+ Add Arrow" then click and drag on the map to draw.
          </p>
        )}
      </div>
    </div>
  );
};

export default MapCanvas;
